import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import './Home.css';

const Home = forwardRef((props, ref) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState({ start: null, target: null, time: null });
  const [expandedTripId, setExpandedTripId] = useState(null);

  useImperativeHandle(ref, () => ({
    handleApplyFilters: (filters) => {
      setActiveFilters(filters);
    }
  }));

  useEffect(() => {
    const fetchStationsAndTrips = async () => {
      try {
        // First, check if stations are in localStorage
        let stationsMap = JSON.parse(localStorage.getItem('stationsMap'));
        
        // If not, fetch from Firestore and store in localStorage
        if (!stationsMap) {
          const stationsCollection = collection(db, 'stations');
          const stationsSnapshot = await getDocs(stationsCollection);
          stationsMap = {};
          stationsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            stationsMap[data.id] = data.name;
          });
          localStorage.setItem('stationsMap', JSON.stringify(stationsMap));
        }

        // Fetch trips
        const tripsCollection = collection(db, 'trips');
        let queryConstraints = [];

        // Add location filter constraints
        if (activeFilters.start) {
          queryConstraints.push(where('start_destination_id', 'in', activeFilters.start));
        }
        if (activeFilters.target) {
          queryConstraints.push(where('target_destination_id', 'in', activeFilters.target));
        }

        // Add time filter constraints
        if (activeFilters.time) {
          if (activeFilters.time.type === 'monat' && activeFilters.time.value.length > 0) {
            // Get all trips and filter by month client-side
            // (Firestore doesn't support extracting month from timestamp)
          } else if (activeFilters.time.type === 'exaktes datum') {
            if (activeFilters.time.value.from) {
              queryConstraints.push(where('start_time', '>=', activeFilters.time.value.from));
            }
            if (activeFilters.time.value.to) {
              queryConstraints.push(where('end_time', '<=', activeFilters.time.value.to + 'T23:59:59'));
            }
          }
        }

        // Create query with constraints
        const q = queryConstraints.length > 0 
          ? query(tripsCollection, ...queryConstraints)
          : tripsCollection;

        const tripSnapshot = await getDocs(q);
        let tripsList = tripSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Apply month filtering client-side if needed
        if (activeFilters.time?.type === 'monat' && activeFilters.time.value.length > 0) {
          tripsList = tripsList.filter(trip => {
            const tripMonth = new Date(trip.start_time).getMonth();
            return activeFilters.time.value.includes(tripMonth);
          });
        }

        setTrips(tripsList);
      } catch (error) {
        console.error("Error fetching data: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStationsAndTrips();
  }, [activeFilters]);

  const getStationName = (stationId) => {
    const stationsMap = JSON.parse(localStorage.getItem('stationsMap')) || {};
    return stationsMap[stationId] || `Station ${stationId}`;
  };

  const getMonthColor = (month) => {
    // Map months to values between 0 and 1 (0 being coldest, 1 being hottest)
    const monthTemperatureMap = {
      0: 0.2,  // January
      1: 0.3,  // February
      2: 0.4,  // March
      3: 0.5,  // April
      4: 0.7,  // May
      5: 0.8,  // June
      6: 1.0,  // July
      7: 1.0,  // August
      8: 0.8,  // September
      9: 0.6,  // October
      10: 0.4, // November
      11: 0.2, // December
    };

    const value = monthTemperatureMap[month];
    
    // Interpolate between blue (cold) and orange (warm)
    const cold = [0, 122, 255];    // Pure blue
    const warm = [255, 122, 0];    // Pure orange
    
    const r = Math.round(cold[0] + (warm[0] - cold[0]) * value);
    const g = Math.round(cold[1] + (warm[1] - cold[1]) * value);
    const b = Math.round(cold[2] + (warm[2] - cold[2]) * value);
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  const getMonthChip = (dateString) => {
    const date = new Date(dateString);
    const monthName = date.toLocaleString('default', { month: 'long' });
    const monthNumber = date.getMonth();
    const backgroundColor = getMonthColor(monthNumber);

    return (
      <span 
        className="month-chip" 
        style={{ 
          backgroundColor,
          color: '#ffffff'  // Always use white text
        }}
      >
        {monthName}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const handleTripClick = (tripId) => {
    setExpandedTripId(expandedTripId === tripId ? null : tripId);
  };

  if (loading) {
    return <div>Loading trips...</div>;
  }

  return (
    <div className="home">
      <h2>Ralleeeeeeeys</h2>
      <div className="trips-list">
        {trips.map((trip) => (
          <div 
            key={trip.id} 
            className={`trip-item ${expandedTripId === trip.id ? 'expanded' : ''}`}
            onClick={() => handleTripClick(trip.id)}
          >
            <div className="trip-summary">
              {getStationName(trip.start_destination_id)} → {getStationName(trip.target_destination_id)} {getMonthChip(trip.start_time)}
            </div>
            {expandedTripId === trip.id && (
              <div className="trip-details">
                <div className="trip-time">
                  <span>Start: {formatDate(trip.start_time)}</span>
                  <span>Ende: {formatDate(trip.end_time)}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default Home; 